import { useState, type FormEvent } from "react";
import { PlanGate } from "@/components/billing/PlanGate";
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  ExternalLink,
  Link2,
  Loader2,
  Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/PageHeader";
import { useLinks } from "@/lib/hooks/useLinks";
import { redirectBaseUrl, shortLinkUrl } from "@/lib/short-links";
import { callErrorMessage, ConvertLinkError, shortenUrl } from "@/lib/hooks/useConvertLink";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import type { ClickEvent } from "@/lib/types";

// ---------------------------------------------------------------------------
// Tipos locais
// ---------------------------------------------------------------------------

type NoticeTone = "neutral" | "success" | "warning" | "danger";

interface ShortLink {
  id: string;
  shortCode: string;
  originalUrl: string;
  destinationUrl: string;
  createdAt: string;
  clicks: number;
}

// ---------------------------------------------------------------------------
// Hook: buscar cliques por link (ultimos 30 dias)
// ---------------------------------------------------------------------------

const CLICK_EVENTS_QUERY_KEY = ["click-events"] as const;

async function fetchClickEvents(): Promise<ClickEvent[]> {
  const { data, error } = await supabase
    .from("click_events")
    .select("link_id, clicked_at")
    .gte("clicked_at", new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());

  if (error) throw error;
  return (data ?? []) as ClickEvent[];
}

function groupClicksByLink(events: ClickEvent[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const event of events) {
    map.set(event.link_id, (map.get(event.link_id) ?? 0) + 1);
  }
  return map;
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

function EncurtadorPageContent(): JSX.Element {
  const queryClient = useQueryClient();
  const { data: links = [], isLoading, error: linksError } = useLinks();
  const { data: clickEvents = [] } = useQuery<ClickEvent[], Error>({
    queryKey: CLICK_EVENTS_QUERY_KEY,
    queryFn: fetchClickEvents,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const clicksByLink = groupClicksByLink(clickEvents);

  // Filtra apenas links com short_code (encurtados)
  const shortLinks: ShortLink[] = links
    .filter((link) => link.short_code && link.mode === "shortener")
    .map((link) => ({
      id: link.id,
      shortCode: link.short_code!,
      originalUrl: link.original_url,
      destinationUrl: link.destination_url,
      createdAt: link.created_at,
      clicks: clicksByLink.get(link.id) ?? 0,
    }));

  // Estado do formulario de criar novo link encurtado
  const [showForm, setShowForm] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [generating, setGenerating] = useState(false);
  const [notice, setNotice] = useState<{ tone: NoticeTone; message: string } | null>(null);
  const [lastShortCode, setLastShortCode] = useState<string | null>(null);
  const [copyingCode, setCopyingCode] = useState<string | null>(null);

  const handleCreateShortLink = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setNotice(null);

    const trimmedUrl = urlInput.trim();
    if (!trimmedUrl) {
      setNotice({ tone: "danger", message: "Cole a URL que deseja encurtar." });
      return;
    }

    setGenerating(true);

    try {
      const { shortUrl } = await shortenUrl(trimmedUrl);
      const shortCode = /\/r\/([A-Za-z0-9]+)$/.exec(shortUrl)?.[1];
      if (!shortCode) {
        throw new Error("O servidor não devolveu o link encurtado. Tente de novo.");
      }

      setLastShortCode(shortCode);
      setNotice({ tone: "success", message: "Link encurtado criado com sucesso!" });
      setUrlInput("");
      await queryClient.invalidateQueries({ queryKey: ["links"] });
    } catch (error) {
      setNotice({
        tone: "danger",
        message: shortenerErrorMessage(error),
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async (code: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(shortLinkUrl(code));
      setCopyingCode(code);
      setTimeout(() => setCopyingCode(null), 1500);
    } catch {
      setNotice({ tone: "danger", message: "Não foi possível copiar. Selecione o link e copie manualmente." });
    }
  };

  const shortUrlBase = `${redirectBaseUrl()}/r`;

  return (
    <div className="max-w-6xl space-y-6">
      <PageHeader
        title="Encurtador"
        subtitle="Links encurtados para compartilhar em qualquer lugar."
        actions={
          <Button type="button" onClick={() => setShowForm((prev) => !prev)}>
            <Plus size={15} />
            {showForm ? "Fechar" : "Encurtar novo link"}
          </Button>
        }
      />

      {linksError && (
        <div className="flex items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <AlertCircle size={16} className="shrink-0" />
          <span>Erro ao carregar links: {linksError.message}</span>
        </div>
      )}

      {/* Formulario para criar novo link encurtado */}
      {showForm && (
        <Card variant="raised" className="overflow-hidden">
          <CardHeader className="border-b border-border bg-[hsl(var(--surface-raised)/0.45)]">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Link2 size={18} />
              </div>
              <div className="space-y-1">
                <CardTitle>Encurtar novo link</CardTitle>
                <CardDescription>
                  Cole a URL que deseja encurtar. O link gerado redireciona para o destino original.
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-4 pt-5">
            <form className="space-y-4" onSubmit={handleCreateShortLink}>
              <div className="space-y-2">
                <Label htmlFor="shortener-url" variant="field">
                  URL original
                </Label>
                <Input
                  id="shortener-url"
                  type="url"
                  value={urlInput}
                  onChange={(event) => setUrlInput(event.target.value)}
                  placeholder="https://www.exemplo.com/produto"
                  autoComplete="off"
                />
              </div>

              <Button type="submit" disabled={generating}>
                {generating ? <Loader2 size={15} className="animate-spin" /> : <Link2 size={15} />}
                Gerar link encurtado
              </Button>
            </form>

            {notice && (
              <div
                className={cn(
                  "flex items-start gap-3 rounded-lg border px-4 py-3 text-sm",
                  notice.tone === "success"
                    ? "border-primary/25 bg-primary/10 text-primary"
                    : notice.tone === "danger"
                      ? "border-destructive/30 bg-destructive/10 text-destructive"
                      : "border-border bg-[hsl(var(--surface)/0.75)] text-muted-foreground",
                )}
              >
                {notice.tone === "success" ? (
                  <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
                ) : (
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                )}
                <span>{notice.message}</span>
              </div>
            )}

            {lastShortCode && (
              <div className="rounded-lg border border-[hsl(var(--border-strong))] bg-[hsl(var(--surface-overlay)/0.75)] p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                      Link encurtado
                    </p>
                    <p className="font-mono text-sm text-foreground break-all">
                      {shortUrlBase}/{lastShortCode}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => handleCopy(lastShortCode)}
                    >
                      {copyingCode === lastShortCode ? (
                        <CheckCircle2 size={14} />
                      ) : (
                        <Copy size={14} />
                      )}
                      {copyingCode === lastShortCode ? "Copiado!" : "Copiar"}
                    </Button>
                    <Button asChild size="sm" variant="secondary">
                      <a
                        href={`${shortUrlBase}/${lastShortCode}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Abrir
                        <ExternalLink size={14} />
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabela de links encurtados */}
      <Card className="overflow-hidden">
        <CardHeader className="border-b border-border bg-[hsl(var(--surface-raised)/0.45)]">
          <CardTitle>Links encurtados</CardTitle>
          <CardDescription>
            {shortLinks.length} {shortLinks.length === 1 ? "link encurtado" : "links encurtados"} no total.
          </CardDescription>
        </CardHeader>

        <CardContent className="p-0">
          {isLoading && (
            <div className="space-y-2 p-5" aria-busy="true" aria-label="Carregando links">
              {Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className="h-16 w-full animate-pulse rounded-xl border border-border bg-[hsl(var(--surface-raised)/0.35)]"
                />
              ))}
            </div>
          )}

          {!isLoading && shortLinks.length === 0 && !linksError && (
            <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Link2 size={24} />
              </div>
              <div className="max-w-md space-y-2">
                <p className="font-semibold text-foreground">Nenhum link encurtado ainda</p>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  Clique em "Encurtar novo link" para criar seu primeiro link encurtado.
                </p>
              </div>
              <Button type="button" onClick={() => setShowForm(true)}>
                <Plus size={15} />
                Encurtar novo link
              </Button>
            </div>
          )}

          {!isLoading && shortLinks.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-[hsl(var(--surface-raised)/0.3)]">
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                      Código
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                      URL original
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                      Cliques (30 dias)
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                      Criado em
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {shortLinks.map((item) => (
                    <tr
                      key={item.id}
                      className="transition-colors hover:bg-[hsl(var(--surface-raised)/0.28)]"
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-medium text-foreground">
                            {item.shortCode}
                          </span>
                        </div>
                      </td>
                      <td className="max-w-[280px] px-5 py-3">
                        <p className="truncate text-muted-foreground" title={item.originalUrl}>
                          {item.originalUrl}
                        </p>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <span className="font-mono text-sm font-medium text-foreground">
                          {item.clicks}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <span className="text-xs text-muted-foreground">
                          {formatDate(item.createdAt)}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-8 px-2"
                            onClick={() => handleCopy(item.shortCode)}
                            title="Copiar link encurtado"
                          >
                            {copyingCode === item.shortCode ? (
                              <CheckCircle2 size={14} className="text-primary" />
                            ) : (
                              <Copy size={14} />
                            )}
                          </Button>
                          <Button
                            asChild
                            size="sm"
                            variant="ghost"
                            className="h-8 px-2"
                          >
                            <a
                              href={`${shortUrlBase}/${item.shortCode}`}
                              target="_blank"
                              rel="noreferrer"
                              title="Abrir link"
                            >
                              <ExternalLink size={14} />
                            </a>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Utilitarios
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  try {
    const date = new Date(iso);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

const SHORTENER_ERRORS: Record<string, string> = {
  invalid_url: "Use uma URL completa começando com http:// ou https://.",
  short_link_loop: "Esse endereço já é um link curto do LucraLink.",
  store_shortener_forbidden:
    "Amazon, Mercado Livre e Magalu não permitem encurtador. Gere o link direto em Gerar link.",
  tenant_not_found: "Conta não encontrada. Entre de novo no painel.",
  short_code_collision: "Não foi possível gerar um código agora. Tente de novo.",
  short_link_daily_limit: "Você atingiu o limite de 100 links avulsos por dia. Tente amanhã.",
};

function shortenerErrorMessage(error: unknown): string {
  if (error instanceof ConvertLinkError) {
    return SHORTENER_ERRORS[error.code] ?? callErrorMessage(error);
  }
  return error instanceof Error ? error.message : "Não foi possível criar o link encurtado.";
}

/** Encurtador: recurso PRO (gate de plano; enforcement no banco). */
export function EncurtadorPage(): JSX.Element {
  return (
    <PlanGate feature="shortener" title="Encurtador" subtitle="Links encurtados para compartilhar em qualquer lugar.">
      <EncurtadorPageContent />
    </PlanGate>
  );
}
